import { MuiTelInput } from 'mui-tel-input';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { useField, validators } from '@toolbox/form';
import MiuThemeProvider from '@toolbox/style/theme/common/ThemeProvider';
import { ThemeProvider } from '@toolbox/style';
import { InputWrapper } from '@toolbox/components/Input/InputWrapper';
import { useMarketData } from '@widget/core';

import type { ReactNode } from 'react';
import type { MuiTelInputProps, MuiTelInputInfo } from 'mui-tel-input';
import type { FieldPath, FieldValues, UseFieldProps } from '@toolbox/form';
import type { InputWrapperProps } from '@toolbox/components/Input/InputWrapper';

export type PhoneNumberFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<InputWrapperProps, 'error' | 'onChange' | 'name' | 'defaultValue'> &
  UseFieldProps<TFieldValues, TName> &
  MuiTelInputProps & {
    defaultErrorMessage?: string;
    value?: string;
    keepErrorSpace?: boolean;
    suffix?: ReactNode;
    additionalValidation?: boolean;
  };

type CountryCode = 'DE' | 'AT';

export const PhoneNumberField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  keepErrorSpace = true,
  defaultErrorMessage = 'Telefonnummer ist ungültig',
  defaultValue,
  required,
  rules,
  control,
  tooltip,
  showTooltip,
  tooltipPlacement = 'bottom-end',
  tooltipIcon,
  suffix,
  color,
  css,
  inputRef,
  additionalValidation = false,
  ...props
}: PhoneNumberFieldProps<TFieldValues, TName>): JSX.Element => {
  const { market } = useMarketData();
  const { setError, clearErrors } = useFormContext();
  const { field, fieldState } = useField<TFieldValues, TName>({
    name,
    defaultValue,
    rules: {
      required,
      validate: {
        phoneNumber: validators.phoneNumber({
          message: defaultErrorMessage,
          useApi: additionalValidation,
        }),
      },
      ...rules,
    },
    control,
  });
  const [phone, setPhone] = useState('');
  const [isTouched, setIsTouched] = useState(!!field.value);

  const error = fieldState.error?.message || (fieldState.invalid && defaultErrorMessage);
  const handleInputChange = (_: string, info: MuiTelInputInfo) => {
    setPhone(info.numberValue || '');

    if (validators.validPhoneByRegexp(info.numberValue || '')) {
      return clearErrors(name);
    }
    setError(name, { message: defaultErrorMessage });
  };

  const handleBlur = async () => {
    field.onChange(phone);
  };

  return (
    <div>
      <InputWrapper
        error={error}
        keepErrorSpace={keepErrorSpace}
        showTooltip={showTooltip}
        tooltip={tooltip}
        tooltipPlacement={tooltipPlacement}
        tooltipIcon={tooltipIcon}
        name={name}
      >
        <MiuThemeProvider>
          <MuiTelInput
            defaultCountry={(market?.toUpperCase() as CountryCode) || 'DE'}
            preferredCountries={['DE', 'AT', 'FR', 'PL', 'ES']}
            {...field}
            {...props}
            value={phone}
            onFocus={() => setIsTouched(true)}
            inputMode="tel"
            onChange={handleInputChange}
            fullWidth
            onBlur={handleBlur}
            forceCallingCode
            error={!!error}
            InputProps={{
              endAdornment: <ThemeProvider>{suffix}</ThemeProvider>,
              className: fieldState.isTouched ? 'active' : undefined,
              ...(!isTouched ? { startAdornment: null } : {}),
              inputRef,
            }}
            MenuProps={{
              autoFocus: false,
              disableAutoFocusItem: true,
              disableEnforceFocus: true,
              disableAutoFocus: true,
              MenuListProps: {
                sx: {
                  padding: '8px!important',
                },
              },
              anchorPosition: { top: 50, left: 50 },
              anchorOrigin: { vertical: 'top', horizontal: 'left' },
              transformOrigin: { vertical: 'top', horizontal: 'left' },
              marginThreshold: 16,
              disableScrollLock: true,
            }}
          />
        </MiuThemeProvider>
      </InputWrapper>
    </div>
  );
};
